<template>
  <div id="actualites">
    <!-- Hero Section -->

    <section
      class="relative text-white pt-16 min-h-screen flex items-center overflow-hidden"
    >
      <!-- Background -->
      <div class="absolute inset-0 hero-gradient"></div>
      <div class="absolute inset-0 bg-black bg-opacity-30"></div>

      <!-- Floating elements -->
      <div
        class="absolute top-20 left-10 w-20 h-20 bg-white bg-opacity-10 rounded-full floating-element"
      ></div>
      <div
        class="absolute top-40 right-20 w-16 h-16 bg-white bg-opacity-10 rounded-full floating-element"
        style="animation-delay: 1s"
      ></div>
      <div
        class="absolute bottom-40 left-20 w-12 h-12 bg-white bg-opacity-10 rounded-full floating-element"
        style="animation-delay: 2s"
      ></div>

      <!-- Content -->
      <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <div class="text-center lg:text-left">
            <div class="mb-6">
              <span
                class="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide animate-pulse-slow"
              >
                🎉 Événement Exceptionnel
              </span>
            </div>

            <h1 class="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up">
              <span class="block">Plongez dans l'univers</span>
              <span class="block gradient-text">médical d'exception</span>
              <span class="block">à Oran !</span>
            </h1>

            <p class="text-xl md:text-2xl opacity-90 mb-8 animate-fade-in-up">
              Les <strong>1ères Journées AMGO/SAMG</strong> vous réservent une
              expérience inoubliable
            </p>

            <div
              class="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mb-8"
            >
              <button
                @click="scrollToInscription"
                class="cta-button text-white px-8 py-4 rounded-lg font-bold text-lg animate-bounce-slow"
              >
                🚀 S'inscrire maintenant
              </button>
              <button
                @click="scrollToProgramme"
                class="bg-white bg-opacity-20 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-opacity-30 transition-all"
              >
                📋 Découvrir le programme
              </button>
            </div>

            <div
              class="flex items-center justify-center lg:justify-start space-x-6 text-sm opacity-90"
            >
              <div class="flex items-center">
                <svg
                  class="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  ></path>
                </svg>
                15-16 Mai 2025
              </div>
              <div class="flex items-center">
                <svg
                  class="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  ></path>
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  ></path>
                </svg>
                Faculté de Médecine d'Oran
              </div>
            </div>
          </div>

          <div class="text-center">
            <div
              class="bg-white bg-opacity-10 p-8 rounded-2xl backdrop-blur-sm"
            >
              <h3 class="text-2xl font-bold mb-6">⏰ Compte à rebours</h3>
              <div id="countdown" class="grid grid-cols-4 gap-4 mb-6">
                <div class="countdown-box">
                  <div class="text-2xl font-bold" id="days">--</div>
                  <div class="text-xs">Jours</div>
                </div>
                <div class="countdown-box">
                  <div class="text-2xl font-bold" id="hours">--</div>
                  <div class="text-xs">Heures</div>
                </div>
                <div class="countdown-box">
                  <div class="text-2xl font-bold" id="minutes">--</div>
                  <div class="text-xs">Minutes</div>
                </div>
                <div class="countdown-box">
                  <div class="text-2xl font-bold" id="seconds">--</div>
                  <div class="text-xs">Secondes</div>
                </div>
              </div>
              <p class="text-sm opacity-90">Jusqu'au début de l'événement</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Informations essentielles -->
    <section class="py-16 bg-white">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-200"
        >
          <div class="text-center mb-8">
            <h2 class="text-3xl font-bold text-gray-900 mb-4">
              🏥 Événement Médical de Premier Plan
            </h2>
            <p class="text-gray-600 max-w-3xl mx-auto">
              L'Association des Médecins Généralistes-Médecins de Famille d'Oran
              (AMGO/SAMG) vous convie à un événement novateur qui marie science,
              passion et partage.
            </p>
          </div>

          <div class="grid md:grid-cols-3 gap-8">
            <div class="text-center">
              <div
                class="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  ></path>
                </svg>
              </div>
              <h3 class="text-xl font-bold text-gray-900 mb-2">📅 Dates</h3>
              <p class="text-gray-600">15 et 16 mai 2025</p>
              <p class="text-sm text-gray-500">
                Deux jours puissants dédiés aux médecins généralistes
              </p>
            </div>

            <div class="text-center">
              <div
                class="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  ></path>
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  ></path>
                </svg>
              </div>
              <h3 class="text-xl font-bold text-gray-900 mb-2">📍 Lieu</h3>
              <p class="text-gray-600">Faculté de Médecine d'Oran</p>
              <p class="text-sm text-gray-500">Cadre académique prestigieux</p>
            </div>

            <div class="text-center">
              <div
                class="bg-purple-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  ></path>
                </svg>
              </div>
              <h3 class="text-xl font-bold text-gray-900 mb-2">🏛️ Patronage</h3>
              <p class="text-gray-600">Haut patronage du Wali d'Oran</p>
              <p class="text-sm text-gray-500">
                Soutien institutionnel prestigieux
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Pourquoi participer -->
    <section id="pourquoi" class="py-16 bg-gray-50">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-gray-900 mb-4">
            🌟 Pourquoi Participer ?
          </h2>
          <p class="text-gray-600 max-w-2xl mx-auto">
            Trois raisons exceptionnelles de rejoindre cette aventure médicale
            unique
          </p>
        </div>

        <div class="grid lg:grid-cols-3 gap-8">
          <!-- Conférences d'Exception -->
          <div class="section-card bg-white rounded-xl shadow-lg p-8">
            <div
              class="feature-icon w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <svg
                class="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                ></path>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-4 text-center">
              💡 Conférences d'Exception
            </h3>
            <p class="text-gray-600 leading-relaxed mb-6">
              Laissez-vous inspirer par des experts de renom qui dévoileront les
              dernières avancées et innovations en médecine. Une chance
              d'élargir vos connaissances et de repenser votre pratique au
              quotidien.
            </p>
            <div class="bg-blue-50 p-4 rounded-lg">
              <ul class="text-sm text-blue-700 space-y-1">
                <li>✓ Experts internationaux</li>
                <li>✓ Dernières innovations</li>
                <li>✓ Pratiques révolutionnaires</li>
                <li>✓ Sessions interactives</li>
              </ul>
            </div>
          </div>

          <!-- Networking Dynamique -->
          <div class="section-card bg-white rounded-xl shadow-lg p-8">
            <div
              class="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <svg
                class="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                ></path>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-4 text-center">
              🤝 Networking Dynamique
            </h3>
            <p class="text-gray-600 leading-relaxed mb-6">
              Échangez, partagez et créez des connexions durables avec vos pairs
              dans un cadre stimulant propice aux rencontres professionnelles et
              aux collaborations fructueuses.
            </p>
            <div class="bg-green-50 p-4 rounded-lg">
              <ul class="text-sm text-green-700 space-y-1">
                <li>✓ Rencontres professionnelles</li>
                <li>✓ Échanges d'expériences</li>
                <li>✓ Collaborations futures</li>
                <li>✓ Communauté active</li>
              </ul>
            </div>
          </div>

          <!-- Impact Médical -->
          <div class="section-card bg-white rounded-xl shadow-lg p-8">
            <div
              class="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <svg
                class="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                ></path>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-4 text-center">
              🚀 Impactez l'Avenir Médical
            </h3>
            <p class="text-gray-600 leading-relaxed mb-6">
              Participez activement à la valorisation du rôle indispensable des
              médecins généralistes dans la prise en charge globale des
              patients. Votre expertise contribue à façonner l'avenir des soins
              de santé.
            </p>
            <div class="bg-red-50 p-4 rounded-lg">
              <ul class="text-sm text-red-700 space-y-1">
                <li>✓ Valorisation de la profession</li>
                <li>✓ Innovation en soins primaires</li>
                <li>✓ Influence sur les politiques</li>
                <li>✓ Leadership médical</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Programme -->
    <section id="programme" class="py-16 bg-white">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-gray-900 mb-4">
            📋 Découvrez le Programme
          </h2>
          <p class="text-gray-600 max-w-2xl mx-auto">
            Un programme riche et varié conçu pour maximiser votre expérience
            d'apprentissage
          </p>
        </div>

        <div
          class="bg-gradient-to-br from-slate-600 to-slate-700 text-white rounded-2xl p-8 mb-8"
        >
          <div class="text-center mb-8">
            <h3 class="text-2xl font-bold mb-4">🎯 Thème Central</h3>
            <p class="text-xl font-light opacity-90">
              "Innovations et défis pour des soins de qualité"
            </p>
          </div>

          <div class="grid md:grid-cols-2 gap-8">
            <div class="bg-white bg-opacity-10 p-6 rounded-lg">
              <h4 class="text-xl font-bold mb-4">📅 Jour 1 - 15 Mai 2025</h4>
              <ul class="space-y-2 text-sm opacity-90">
                <li>🌅 <strong>09:00</strong> - Accueil et inscription</li>
                <li>🎤 <strong>10:00</strong> - Cérémonie d'ouverture</li>
                <li>💡 <strong>11:00</strong> - Conférences magistrales</li>
                <li>🍽️ <strong>12:30</strong> - Déjeuner networking</li>
                <li>🔬 <strong>14:00</strong> - Ateliers pratiques</li>
                <li>💬 <strong>16:00</strong> - Tables rondes</li>
                <li>🎉 <strong>18:00</strong> - Cocktail de bienvenue</li>
              </ul>
            </div>

            <div class="bg-white bg-opacity-10 p-6 rounded-lg">
              <h4 class="text-xl font-bold mb-4">📅 Jour 2 - 16 Mai 2025</h4>
              <ul class="space-y-2 text-sm opacity-90">
                <li>🌅 <strong>09:00</strong> - Sessions thématiques</li>
                <li>🎯 <strong>10:30</strong> - Cas cliniques interactifs</li>
                <li>🍽️ <strong>12:00</strong> - Déjeuner d'affaires</li>
                <li>🚀 <strong>13:30</strong> - Innovations technologiques</li>
                <li>
                  🤝 <strong>15:00</strong> - Partenariats et collaborations
                </li>
                <li>🏆 <strong>16:30</strong> - Remise des prix</li>
                <li>🎊 <strong>17:30</strong> - Cérémonie de clôture</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="grid md:grid-cols-3 gap-6">
          <div class="bg-blue-50 p-6 rounded-lg border border-blue-200">
            <div class="text-center mb-4">
              <div
                class="bg-blue-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
              >
                <svg
                  class="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  ></path>
                </svg>
              </div>
              <h4 class="font-bold text-blue-900">Conférences Magistrales</h4>
            </div>
            <p class="text-sm text-blue-700 text-center">
              Interventions d'experts reconnus sur les dernières avancées
              médicales
            </p>
          </div>

          <div class="bg-green-50 p-6 rounded-lg border border-green-200">
            <div class="text-center mb-4">
              <div
                class="bg-green-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
              >
                <svg
                  class="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  ></path>
                </svg>
              </div>
              <h4 class="font-bold text-green-900">Ateliers Pratiques</h4>
            </div>
            <p class="text-sm text-green-700 text-center">
              Sessions hands-on pour développer vos compétences pratiques
            </p>
          </div>

          <div class="bg-purple-50 p-6 rounded-lg border border-purple-200">
            <div class="text-center mb-4">
              <div
                class="bg-purple-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
              >
                <svg
                  class="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  ></path>
                </svg>
              </div>
              <h4 class="font-bold text-purple-900">Networking</h4>
            </div>
            <p class="text-sm text-purple-700 text-center">
              Moments privilégiés d'échanges avec vos confrères
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Inscription -->
    <section
      id="inscription"
      class="py-16 bg-gradient-to-br from-red-500 to-red-600 text-white"
    >
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-3xl font-bold mb-6">
          🚀 Inscrivez-vous dès maintenant !
        </h2>
        <p class="text-xl mb-8 opacity-90">
          Réservez votre place pour ces deux journées exceptionnelles de partage
          et d'inspiration
        </p>

        <div
          class="bg-white bg-opacity-10 p-8 rounded-2xl backdrop-blur-sm mb-8"
        >
          <h3 class="text-2xl font-bold mb-6">📝 Formulaire d'Inscription</h3>
          <p class="mb-6 opacity-90">
            Cliquez sur le bouton ci-dessous pour accéder au formulaire
            d'inscription officiel
          </p>
          <button
            @click="openRegistrationForm"
            class="bg-white text-red-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all transform hover:scale-105"
          >
            📋 Accéder au formulaire
          </button>
        </div>

        <div class="grid md:grid-cols-2 gap-8">
          <div class="bg-white bg-opacity-10 p-6 rounded-lg">
            <h4 class="text-xl font-bold mb-4">📱 Contact WhatsApp</h4>
            <p class="mb-4 opacity-90">Pour toute question ou assistance</p>
            <a
              href="https://wa.me/213671153216"
              target="_blank"
              class="bg-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors inline-flex items-center"
            >
              <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"
                />
              </svg>
              0671 15 32 16
            </a>
          </div>

          <div class="bg-white bg-opacity-10 p-6 rounded-lg">
            <h4 class="text-xl font-bold mb-4">📧 Contact Email</h4>
            <p class="mb-4 opacity-90">Pour les demandes officielles</p>
            <a
              href="mailto:amgo31dz@gmail.com"
              class="bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors inline-flex items-center"
            >
              <svg
                class="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                ></path>
              </svg>
              amgo31dz@gmail.com
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Découvrir Oran -->
    <section class="py-16 bg-gradient-to-br from-orange-50 to-amber-50">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-gray-900 mb-4">
            🌅 Bienvenue à Oran !
          </h2>
          <p class="text-gray-600 max-w-2xl mx-auto">
            La vibrante capitale de l'ouest algérien vous accueille
          </p>
        </div>

        <div class="bg-white rounded-2xl shadow-lg p-8">
          <div class="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 class="text-2xl font-bold text-gray-900 mb-4">
                🏛️ Une Ville d'Histoire et de Modernité
              </h3>
              <p class="text-gray-600 leading-relaxed mb-6">
                Découvrez non seulement un événement médical de premier plan,
                mais profitez également de l'accueil chaleureux et des moments
                inoubliables offerts par cette ville pleine de vie et
                d'opportunités.
              </p>

              <div class="space-y-4">
                <div class="flex items-center">
                  <div
                    class="bg-orange-500 w-8 h-8 rounded-full flex items-center justify-center mr-3"
                  >
                    <svg
                      class="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span class="text-gray-700"
                    >Patrimoine architectural exceptionnel</span
                  >
                </div>
                <div class="flex items-center">
                  <div
                    class="bg-orange-500 w-8 h-8 rounded-full flex items-center justify-center mr-3"
                  >
                    <svg
                      class="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span class="text-gray-700"
                    >Gastronomie méditerranéenne authentique</span
                  >
                </div>
                <div class="flex items-center">
                  <div
                    class="bg-orange-500 w-8 h-8 rounded-full flex items-center justify-center mr-3"
                  >
                    <svg
                      class="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span class="text-gray-700"
                    >Hospitalité légendaire des Oranais</span
                  >
                </div>
                <div class="flex items-center">
                  <div
                    class="bg-orange-500 w-8 h-8 rounded-full flex items-center justify-center mr-3"
                  >
                    <svg
                      class="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span class="text-gray-700"
                    >Centre médical et universitaire de référence</span
                  >
                </div>
              </div>
            </div>

            <div class="text-center">
              <div
                class="bg-gradient-to-br from-orange-400 to-red-500 p-8 rounded-2xl text-white"
              >
                <h4 class="text-xl font-bold mb-4">🎯 Votre Avenir Médical</h4>
                <p class="mb-6 opacity-90">
                  Votre avenir médical se dessine ici – ne manquez pas cette
                  chance unique de transformer votre pratique et d'inspirer le
                  monde de demain !
                </p>
                <div class="bg-white bg-opacity-20 p-4 rounded-lg">
                  <p class="text-sm font-semibold">
                    Bureau AMGO<br />
                    Association des Médecins Généralistes<br />
                    Médecins de Famille d'Oran
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- WhatsApp Button -->
    <a
      href="https://wa.me/213671153216"
      target="_blank"
      class="whatsapp-button"
    >
      <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path
          d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"
        />
      </svg>
    </a>
  </div>
</template>

<script>
export default {
  name: "LegalNotices",
  components: {},
  data() {
    return {};
  },
  mounted() {
    // Mobile menu toggle
    const mobileMenuBtn = document.getElementById("mobile-menu-btn");
    const mobileMenu = document.getElementById("mobile-menu");

    mobileMenuBtn.addEventListener("click", () => {
      mobileMenu.classList.toggle("hidden");
    });

    // Countdown timer
    function updateCountdown() {
      const eventDate = new Date("2025-05-15T09:00:00").getTime();
      const now = new Date().getTime();
      const distance = eventDate - now;

      if (distance > 0) {
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        document.getElementById("days").textContent = days
          .toString()
          .padStart(2, "0");
        document.getElementById("hours").textContent = hours
          .toString()
          .padStart(2, "0");
        document.getElementById("minutes").textContent = minutes
          .toString()
          .padStart(2, "0");
        document.getElementById("seconds").textContent = seconds
          .toString()
          .padStart(2, "0");
      } else if (distance >= -1) {
        document.getElementById("countdown").innerHTML =
          '<div class="text-center text-xl font-bold">🎉 L\'événement a commencé !</div>';
      } else {
        document.getElementById("countdown").innerHTML =
          '<div class="text-center text-xl font-bold">🎉 L\'événement est terminé !</div>';
      }
    }

    // Update countdown every second
    setInterval(updateCountdown, 1000);
    updateCountdown();

    // Animation on scroll
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = "1";
          entry.target.style.transform = "translateY(0)";
        }
      });
    }, observerOptions);

    // Observe cards for animation
    document.querySelectorAll(".section-card").forEach((card) => {
      card.style.opacity = "0";
      card.style.transform = "translateY(20px)";
      card.style.transition = "opacity 0.6s ease, transform 0.6s ease";
      observer.observe(card);
    });

    // Smooth scrolling for internal links
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener("click", function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute("href"));
        if (target) {
          target.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      });
    });
  },
  methods: {
    // Smooth scrolling functions
    scrollToInscription() {
      document.getElementById("inscription").scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },

    scrollToProgramme() {
      document.getElementById("programme").scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },

    // Registration form function
    openRegistrationForm() {
      // This would typically open a real registration form
      // For demo purposes, we'll show an alert
      alert(
        "🚀 Redirection vers le formulaire d'inscription...\n\nVous seriez redirigé vers le formulaire officiel d'inscription pour les 1ères Journées AMGO/SAMG.\n\nPour vous inscrire dès maintenant, contactez-nous :\n📱 WhatsApp: 0671 15 32 16\n📧 Email: amgo31dz@gmail.com"
      );
    },
  },
};
</script>
<style>
.hero-gradient {
  background: linear-gradient(135deg, #4f6bb6 0%, #5a67a8 100%);
}
.smooth-scroll {
  scroll-behavior: smooth;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes bounce {
  0%,
  20%,
  53%,
  80%,
  100% {
    transform: translateY(0);
  }
  40%,
  43% {
    transform: translateY(-10px);
  }
  70% {
    transform: translateY(-5px);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 1s ease-out;
}

.animate-pulse-slow {
  animation: pulse 3s ease-in-out infinite;
}

.animate-bounce-slow {
  animation: bounce 2s infinite;
}

.section-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.section-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px -5px rgba(0, 0, 0, 0.15);
}

.gradient-text {
  background: linear-gradient(135deg, #4f6bb6 0%, #e74c3c 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.cta-button {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(231, 76, 60, 0.3);
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(231, 76, 60, 0.4);
}

.feature-icon {
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
}

.countdown-box {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  border-radius: 12px;
  padding: 1rem;
  color: white;
  text-align: center;
  min-width: 80px;
}

.floating-element {
  animation: bounce 3s ease-in-out infinite;
}

.whatsapp-button {
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4);
  transition: all 0.3s ease;
}

.whatsapp-button:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 25px rgba(37, 211, 102, 0.6);
}
</style>
